import { useEffect, useRef, useState } from "react";
import { gxvalidateApi } from "@/app/apiConfig/services/authAbstractApiLayer";
import { ICON_BASE_PATH } from "../../../../utils/config";
import { TextBox, type TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { t } from "i18next";

interface CompanyNameProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  validationMessage?: string;
  className?: string;
  starlable?: boolean;
  disable?: boolean;
  max?: number;
  setAlreadyRegistered: (value: boolean) => void;
}

const CompanyName: React.FC<CompanyNameProps> = ({
  label = "",
  value,
  onChange,
  validationMessage,
  starlable = true,
  disable = false,
  max,
  setAlreadyRegistered,
}) => {
  const [apiError, setApiError] = useState<string | null>(null);
  const latestRequest = useRef(0);
  const sanitize = (s: string) => s.replace(/[^\p{L}\s]/gu, "");

  const handleChange = (e: TextBoxChangeEvent) => {
    const raw = String(e.value ?? "");
    let filtered = sanitize(raw);

    if (max && filtered.length > max) {
      filtered = filtered.slice(0, max);
    }

    setApiError(null);
    setAlreadyRegistered(false);

    onChange(filtered);
  };

  // ........................fetchcompanynameverity api...................................

  const fetchcompanynameverity = async (name: string) => {
    const requestId = ++latestRequest.current;
    setApiError(null);
    setAlreadyRegistered(false);
    try {
      const results = await gxvalidateApi({ groupCompName: name });
      const data = results as any;
      if (requestId !== latestRequest.current) return;
      if (data?.exists || data?.data?.isDuplicate) {
        setApiError("This company name is already registered.");
        setAlreadyRegistered(true);
      } else {
        setApiError(null);
        setAlreadyRegistered(false);
      }
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      setApiError("This company name is already registered.");
      setAlreadyRegistered(true);
    }
  };


  useEffect(() => {
    if (!value?.trim() || value.trim().length < 3) {
      setApiError(null);
      setAlreadyRegistered(false);
      return;
    }

    const timer = setTimeout(() => {
      fetchcompanynameverity(value);
    }, 500);

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="gx_input_container">
      <label
        className={`input-label gx-font-semibold ${
          value?.trim() ? "floating" : ""
        }`}
      >
        {t(label)}
        {starlable && (
          <span
            className={`gx_input_star ${
              value?.trim() ? "gx_input_star_float" : "gx_input_star_normal"
            }`}
          >
            *
          </span>
        )}
      </label>

      <div
        className={`gx_input_cont ${
          !(validationMessage || apiError) || value ? "valid" : "invalid"
        }`}
      >
        <img src={`${ICON_BASE_PATH}companyicon.svg`} alt="name" />

        <TextBox
          value={value}
          onChange={handleChange}
          type="text"
          autoComplete="off"
          max={max}
          disabled={disable}
          className="gx-textbox-css"
        />
      </div>

      <div
        className="gx_input_validationmessage"
        style={{
          visibility: validationMessage || apiError ? "visible" : "hidden",
        }}
      >
        {validationMessage || apiError || "\u00A0"}
      </div>
    </div>
  );
};

export default CompanyName;
